import { useEffect } from 'react';
import { useFirstMountState, usePrevious } from 'react-use';

import { BindingCreator, FormContextValues } from '@dreryk/react-common';
import { PostcodeInput, TestId, TextInput } from '@dreryk/react-components';

import { referralToPsychiatricHospitalTranslationKeys } from '../../../../translations';

import { CountryField, GuardianAddressCountryFormBeanPart } from './CountryField';
import {
   GuardianFieldName,
   GuardianWithAddress,
   GuardianWithAddressFormBeanPart,
   ReferralToPsychiatricHospitalFormGuardian,
} from './guardianSection.types';
import { getReferralToPsychiatricHospitalFormGuardianAddress } from './guardianSection.utils';

type GuardianAddressSectionProps<FIELD_NAME extends GuardianFieldName> = Pick<
   FormContextValues<GuardianWithAddressFormBeanPart>,
   'watch' | 'setValue' | 'trigger'
> & {
   readonly name: FIELD_NAME;
   readonly testId: TestId;
   readonly register: BindingCreator<GuardianWithAddressFormBeanPart>;
};

const ADDRESS_MAX_LENGTH_SHORT = 20;
const ADDRESS_MAX_LENGTH_LONG = 50;

const isGuardianNotEnteredByUser = (
   guardian: GuardianWithAddress | null | undefined
): guardian is ReferralToPsychiatricHospitalFormGuardian => Boolean(guardian && guardian !== 'ENTERED_BY_USER');
const shouldUpdateGuardianAddress = (
   currentGuardian: GuardianWithAddress | null,
   previousGuardian: GuardianWithAddress | null | undefined
): currentGuardian is ReferralToPsychiatricHospitalFormGuardian => {
   if (
      (!previousGuardian || previousGuardian === 'ENTERED_BY_USER') &&
      currentGuardian !== null &&
      currentGuardian !== 'ENTERED_BY_USER'
   ) {
      return true;
   }
   return (
      isGuardianNotEnteredByUser(previousGuardian) &&
      isGuardianNotEnteredByUser(currentGuardian) &&
      currentGuardian.id !== previousGuardian.id
   );
};

// eslint-disable-next-line max-lines-per-function
export const GuardianAddressSection = <FIELD_NAME extends GuardianFieldName>(
   props: GuardianAddressSectionProps<FIELD_NAME>
): JSX.Element => {
   const addressFieldNamePrefix = `${props.name}Address` as const;
   const guardian = props.watch(props.name) as GuardianWithAddress | null;
   const previousGuardian = usePrevious(guardian);
   const firstMount = useFirstMountState();

   useEffect(() => {
      if (shouldUpdateGuardianAddress(guardian, previousGuardian)) {
         const address = getReferralToPsychiatricHospitalFormGuardianAddress(guardian);
         props.setValue(addressFieldNamePrefix, address);
         /*
          * TODO DREGAB-15042 Poprawić pola używające Postcode i Country, aby móc ustawiać na nich wartości całego adresu
          * Typowanie wiąże się z dużymi przeróbkami komponentu, co nie powinno mieć miejsca,
          * gdyby pola działały poprawnie. Stąd 'as any'
          */
         props.setValue(`${addressFieldNamePrefix}.postcode`, address.postcode as any);
         props.setValue(`${addressFieldNamePrefix}.country`, address.country as any);
      }
      if (guardian === 'ENTERED_BY_USER' && !firstMount) {
         props.setValue(addressFieldNamePrefix, getReferralToPsychiatricHospitalFormGuardianAddress(null));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [guardian]);

   return (
      <>
         <TextInput
            testId={`${props.testId}Street`}
            controller={props.register(`${addressFieldNamePrefix}.street`, {
               required: true,
               maxLength: ADDRESS_MAX_LENGTH_LONG,
            })}
            label={referralToPsychiatricHospitalTranslationKeys.address.street}
         />
         <TextInput
            testId={`${props.testId}Building`}
            controller={props.register(`${addressFieldNamePrefix}.building`, {
               required: true,
               maxLength: ADDRESS_MAX_LENGTH_SHORT,
            })}
            label={referralToPsychiatricHospitalTranslationKeys.address.building}
         />
         <TextInput
            testId={`${props.testId}Flat`}
            controller={props.register(`${addressFieldNamePrefix}.flat`, { maxLength: ADDRESS_MAX_LENGTH_SHORT })}
            label={referralToPsychiatricHospitalTranslationKeys.address.flat}
         />
         <TextInput
            testId={`${props.testId}Town`}
            controller={props.register(`${addressFieldNamePrefix}.town`, {
               required: true,
               maxLength: ADDRESS_MAX_LENGTH_LONG,
            })}
            label={referralToPsychiatricHospitalTranslationKeys.address.town}
         />
         <PostcodeInput
            testId={`${props.testId}Postcode`}
            controller={props.register(`${addressFieldNamePrefix}.postcode`, { required: true })}
            label={referralToPsychiatricHospitalTranslationKeys.address.postcode}
         />
         <CountryField
            testId={props.testId}
            register={props.register}
            guardianAddressFieldName={addressFieldNamePrefix}
            watch={
               (props.watch as unknown) as FormContextValues<
                  GuardianAddressCountryFormBeanPart<typeof addressFieldNamePrefix>
               >['watch']
            }
            trigger={props.trigger}
         />
      </>
   );
};
